import {FormattedMessage, useIntl} from "react-intl";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import '../scss/SurveyHeader.scss';
import {Link, Prompt, useHistory, useLocation, useParams} from "react-router-dom";
import {ReactComponent as BackArrow} from "../svg/back.svg";
import {Modal, Popup, PopupElement, PopupProvider, StatusText, Toast, usePopup} from "../UIElements";
import {
    EditorFocusContext,
    useOriginalSurveyData,
    useOriginalUserSurveyData,
    useSurveyData,
    useUserSurveyData
} from "./SurveyView";
import {
    GetSurveyWithId,
    GetUserBySurvey,
    PostSurveyWithId,
    PostUsersToAnnieUserSurvey,
    UpdateSurveyWithId, UpdateSurveyWithIdAndUpdateContext
} from "../api/APISurvey";
import { updateTimeToServerFormat} from "../Formats";
import _ from "lodash";
import {ReactComponent as LoaderIcon} from "../svg/loader.svg";
import {ReactComponent as RedoIcon} from "../svg/redo.svg";
import {ReactComponent as UndoIcon} from "../svg/undo.svg";
import {ReactComponent as TestIcon} from "../svg/test.svg";
import {useAuthData, useCurrentUserData} from "../App";
import {SurveySummary} from "./SurveySummary";
import {useSnackbar} from "../Content";
import {CopyPopup} from "./Surveys";
import {useDataContext} from "./SurveyContextProvider";
import {
    clearValueFromSessionStorage, flattenConfigObject,
    logSessionStorage,
    reconstructConfigObject,
    removeUnnecessaryKeys
} from "../DataFunctions";
import {TestSurveyModal} from "./TestSurvey";

export default function SurveyHeader() {
    const { value: surveyData } = useDataContext('surveyData')
    const { value: userSurveyData } = useDataContext('userSurveyData')
    const { value: originalSurveyData } = useDataContext('originalSurveyData')
    const { value: originalUserSurveyData } = useDataContext('originalUserSurveyData')

    return <PopupProvider>
        <HeaderRender
            surveyData={surveyData}
            originalSurveyData={originalSurveyData}
            userSurveyData={userSurveyData}
            originalUserSurveyData={originalUserSurveyData}
        />
        <PopupElement />
    </PopupProvider>
}

function HeaderRender({surveyData, originalSurveyData, userSurveyData, originalUserSurveyData}) {
    const intl = useIntl()
    const surveyName = surveyData.config.title
    let name
    if (surveyData.id === "new" && !surveyName) {
        name = intl.formatMessage(
            {
                id: 'survey.header.newSurvey',
                defaultMessage: 'New Survey',
            })
    } else {
        name = surveyName
    }

    let unsavedChanges = JSON.stringify(surveyData) !== JSON.stringify(originalSurveyData) || JSON.stringify(userSurveyData) !== JSON.stringify(originalUserSurveyData)

    return <>
        <div className={"survey-header"}>
            <Link to="/surveys" className={"back"}>
                <BackArrow />
            </Link>
            <h3>{name}</h3>
            <StatusText survey={surveyData}/>
            {unsavedChanges &&
            <p className={"unsaved-changes"}>
                {intl.formatMessage({
                    id: 'survey.header.unsavedChanges',
                    defaultMessage: 'Unsaved changes',
                })}
            </p>
            }
            <SaveEdits />
            <PromptDialog unsavedChanges={unsavedChanges}/>
        </div>

    </>
}

function PromptDialog({unsavedChanges}) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { value: userSurveyData } = useDataContext('userSurveyData')
    const { value: originalSurveyData, setValue: setOriginalSurveyData } = useDataContext('originalSurveyData')
    const { value: originalUserSurveyData, setValue: setOriginalUserSurveyData } = useDataContext('originalUserSurveyData')

    const surveyUpdate = UpdateSurveyWithIdAndUpdateContext(surveyData.id, setSurveyData, setOriginalSurveyData)
    const [showSavePrompt, setShowSavePrompt] = useState(false)
    const [nextLocationPathname, setNextLocationPathname] = useState("")
    const newID = JSON.stringify(new Date().getTime())
    const surveyPost = PostSurveyWithId(newID)
    const annieUserSurveyPost = PostUsersToAnnieUserSurvey()
    const [confirmNavigation, setConfirmNavigation] = useState(false)
    const intl = useIntl()
    let history = useHistory()
    let { surveyId } = useParams()
    let createNewSurvey = surveyId === "new"
    const {authData} = useAuthData()
    const surveyIsDeleted = surveyData.hasOwnProperty("status") && surveyData.status === "DELETED"

    useEffect(() => {
        if (confirmNavigation) {
            clearValueFromSessionStorage('surveyData', surveyData.id)
            clearValueFromSessionStorage('surveyData', 'new')
            clearValueFromSessionStorage('userSurveyData', surveyData.id)
            clearValueFromSessionStorage('userSurveyData', 'new')

            history.push(nextLocationPathname)
        }
        return () => {setConfirmNavigation(false)}
    }, [confirmNavigation])

    const navigateToLink = () => {
        setConfirmNavigation(true)
    }

    const checkForNextLocation = (nextLocation) => {
        setNextLocationPathname(nextLocation.pathname)
        if (confirmNavigation) {
            return true
        }
        if (nextLocation.pathname === "/surveys" || nextLocation.pathname === "/contacts" || nextLocation.pathname === "/users") {
            setShowSavePrompt(true)
            return false
        }
        return true
    }

    const cancelPrompt = () => {
        setShowSavePrompt(false)
    }

    const saveChanges = () => {
        const newSurveyData = {...surveyData}
        newSurveyData.updated = updateTimeToServerFormat(new Date())
        newSurveyData.updatedby = authData.uid
        // Clean branches of excess keys
        const cleanedSurveyData = removeUnnecessaryKeys(newSurveyData)

        if (surveyId === "new") {
            cleanedSurveyData.id = newID
            setSurveyData(cleanedSurveyData)
            const surveyDataForPost = reconstructConfigObject(cleanedSurveyData)
            surveyPost.mutate(surveyDataForPost, {
                onError: () => {
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'toast.saveFailed',
                                defaultMessage: 'The save failed. Please try again.',
                            }),
                        state: "error",
                        autoHide: true,
                    })
                },
                onSuccess: () => {
                    if (userSurveyData.length < 1) {
                        setConfirmNavigation(true)
                        setSnackbarData({
                            show: true,
                            text: intl.formatMessage(
                                {
                                    id: 'toast.saveSuccess',
                                    defaultMessage: 'Survey Saved!',
                                }),
                            state: "success",
                            autoHide: true,
                        })
                    } else {
                        const newAnnieUserSurveyData = [...userSurveyData]
                        newAnnieUserSurveyData.forEach( annieuser => annieuser.survey = newID)
                        annieUserSurveyPost.mutate(newAnnieUserSurveyData, {
                            onError: () => {
                                setSnackbarData({
                                    show: true,
                                    text: intl.formatMessage(
                                        {
                                            id: 'toast.saveFailed',
                                            defaultMessage: 'The save failed. Please try again.',
                                        }),
                                    state: "error",
                                    autoHide: true,
                                })
                            },
                            onSuccess: () => {
                                //setOriginalUserSurveyData(newAnnieUserSurveyData)
                                setConfirmNavigation(true)
                                setSnackbarData({
                                    show: true,
                                    text: intl.formatMessage(
                                        {
                                            id: 'toast.saveSuccess',
                                            defaultMessage: 'Survey Saved!',
                                        }),
                                    state: "success",
                                    autoHide: true,
                                })
                            }
                        })
                    }
                }
            })
        } else {
            const surveyDataForPost = reconstructConfigObject(cleanedSurveyData)
            surveyUpdate.mutate(surveyDataForPost, {
                onError: () => {
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'toast.saveFailed',
                                defaultMessage: 'The save failed. Please try again.',
                            }),
                        state: "error",
                        autoHide: true,
                    })
                },
                onSuccess: () => {
                    if (userSurveyData.length < 1) {
                        setOriginalUserSurveyData(_.cloneDeep(userSurveyData))
                        setConfirmNavigation(true)
                        setSnackbarData({
                            show: true,
                            text: intl.formatMessage(
                                {
                                    id: 'toast.saveSuccess',
                                    defaultMessage: 'Survey Saved!',
                                }),
                            state: "success",
                            autoHide: true,
                        })
                    } else {
                        annieUserSurveyPost.mutate(userSurveyData, {
                            onError: () => {
                                setSnackbarData({
                                    show: true,
                                    text: intl.formatMessage(
                                        {
                                            id: 'toast.saveFailed',
                                            defaultMessage: 'The save failed. Please try again.',
                                        }),
                                    state: "error",
                                    autoHide: true,
                                })
                            },
                            onSuccess: () => {
                                setOriginalUserSurveyData(_.cloneDeep(userSurveyData))
                                setConfirmNavigation(true)
                                setSnackbarData({
                                    show: true,
                                    text: intl.formatMessage(
                                        {
                                            id: 'toast.saveSuccess',
                                            defaultMessage: 'Survey Saved!',
                                        }),
                                    state: "success",
                                    autoHide: true,
                                })
                            }
                        })
                    }

                }
            })
        }
    }


    // TODO: refactor repeated code

    const [showPublishSummary, setShowPublishSummary] = useState(false)
    const [surveyChecked, setSurveyChecked] = useState(false)

    function clearSessionStorage() {
        clearValueFromSessionStorage('surveyData', surveyData.id)
        clearValueFromSessionStorage('surveyData', 'new')
        clearValueFromSessionStorage('userSurveyData', surveyData.id)
        clearValueFromSessionStorage('userSurveyData', 'new')
    }

    const {setSnackbarData} = useSnackbar()

    const saveSurvey = (newSurvey, draft) => {
        const newSurveyData = {...surveyData}
        // Clean branches of excess keys
        const cleanedSurveyData = removeUnnecessaryKeys(newSurveyData)
        cleanedSurveyData.updated = updateTimeToServerFormat(new Date())
        cleanedSurveyData.updatedby = authData.uid
        const surveyDataForPost = reconstructConfigObject(cleanedSurveyData)
        if (draft) {
            cleanedSurveyData.status = "DRAFT"
        } else {
            if (cleanedSurveyData.status === "DRAFT") {
                cleanedSurveyData.status = "SCHEDULED"
            }
        }
        setSurveyData(cleanedSurveyData)
        surveyUpdate.mutate(surveyDataForPost, {
            onError: () => {
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage(
                        {
                            id: 'toast.saveFailed',
                            defaultMessage: 'The save failed. Please try again.',
                        }),
                    state: "error",
                    autoHide: true,
                })
            },
            onSuccess: () => {
                annieUserSurveyPost.mutate(userSurveyData, {
                    onError: () => {
                        setSnackbarData({
                            show: true,
                            text: intl.formatMessage(
                                {
                                    id: 'toast.saveFailed',
                                    defaultMessage: 'The save failed. Please try again.',
                                }),
                            state: "error",
                            autoHide: true,
                        })
                    },
                    onSuccess: () => {
                        setOriginalUserSurveyData(_.cloneDeep(userSurveyData))
                        clearSessionStorage()
                        setShowPublishSummary(false)
                        setSnackbarData({
                            show: true,
                            text: intl.formatMessage(
                                {
                                    id: 'toast.saveSuccess',
                                    defaultMessage: 'Survey Saved!',
                                }),
                            state: "success",
                            autoHide: true,
                        })
                    }
                })
            }
        })
    }

    function saveOrOpenOverview() {
        if (surveyData.status === "DRAFT" || surveyIsDeleted) {
            saveChanges()
        } else {
            setShowSavePrompt(false)
            setShowPublishSummary(true)
        }
    }

    let modalSaveText = intl.formatMessage(
        {
            id: 'survey.save.save',
            defaultMessage: 'Save',
        })
    let checkAndSave = intl.formatMessage({
        id: 'survey.save.checkSave',
        defaultMessage: 'Check & Save',
    })

    const {currentUserData} = useCurrentUserData()
    const userSuperAdmin = currentUserData.superuser && currentUserData.id.endsWith("@annieadvisor.com")

    return <>
        <Prompt
            when={unsavedChanges}
            message={checkForNextLocation}
        />
        {showSavePrompt &&
        <Modal header={intl.formatMessage({
            id: 'survey.unsavedChanges.header',
            defaultMessage: 'Save changes to survey?',
        })}
               text={<FormattedMessage
                   id={"survey.unsavedChanges.text"}
                   defaultMessage={"Do you wish to save changes before leaving page?{br}Any unsaved changes will be lost."}
                   values={{br: <br/>}}/>
               }
               discardText={intl.formatMessage({
                   id: 'survey.unsavedChanges.discard',
                   defaultMessage: "Don't save",
               })}
               confirmText={(surveyData.status === "DRAFT" || surveyIsDeleted) ? modalSaveText : checkAndSave}
               discardAction={navigateToLink}
               closeModal={cancelPrompt}
               confirmAction={saveOrOpenOverview}
        />
        }
        {showPublishSummary &&
        <Modal
            closeModal={()=>setShowPublishSummary(false)}
            header={intl.formatMessage(
                {
                    id: 'survey.overview',
                    defaultMessage: 'Survey Overview',
                })}
            confirmAction={()=>saveSurvey(createNewSurvey)}
            confirmText={modalSaveText}
            scrollContent={true}
            confirmDisabled={!surveyChecked}
            disabledText={<>
                {intl.formatMessage({
                id: 'survey.overview.checkErrors',
                defaultMessage: 'Check errors before publishing'
            })}
                {userSuperAdmin &&
                <button className={"override"} onClick={()=>setSurveyChecked(true)}>
                    {intl.formatMessage(
                        {
                            id: 'survey.overview.bypassTests',
                            defaultMessage: 'Skip checks',
                        })}
                </button>
                }
            </>}
        >
            <SurveySummary setSurveyChecked={surveyStatus => setSurveyChecked(surveyStatus)}/>
        </Modal>
        }
    </>
}

function SaveEdits() {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { value: userSurveyData } = useDataContext('userSurveyData')
    const { value: originalSurveyData, setValue: setOriginalSurveyData } = useDataContext('originalSurveyData')
    const { value: originalUserSurveyData, setValue: setOriginalUserSurveyData } = useDataContext('originalUserSurveyData')

    const [buttonPressed, setButtonPressed] = useState("")
    const surveyUpdate = UpdateSurveyWithIdAndUpdateContext(surveyData.id, setSurveyData, setOriginalSurveyData)
    const newID = JSON.stringify(new Date().getTime())
    const surveyPost = PostSurveyWithId(newID)
    const annieUserSurveyPost = PostUsersToAnnieUserSurvey()
    let history = useHistory()
    let { surveyId } = useParams()
    const createNewSurvey = surveyId === "new"
    const surveyIsDraft = surveyData.hasOwnProperty("status") && surveyData.status === "DRAFT"
    const surveyIsScheduled = surveyData.hasOwnProperty("status") && surveyData.status === "SCHEDULED"
    const surveyIsFinished = surveyData.hasOwnProperty("status") && surveyData.status === "FINISHED"
    const surveyIsDeleted = surveyData.hasOwnProperty("status") && surveyData.status === "DELETED"
    const intl = useIntl()
    const [showPublishSummary, setShowPublishSummary] = useState(false)
    const [surveyChecked, setSurveyChecked] = useState(false)
    const [showDuplicatePopup, setShowDuplicatePopup] = useState(false)
    const {authData} = useAuthData()

    const navigateToNewId = (IdForNavi) => {
        if (IdForNavi) {
            clearSessionStorage(IdForNavi)
            history.push(
                {
                    pathname: "/survey/"+IdForNavi,
                    state: {surveyDuplicated: true}
                }
            )
        } else {
            console.log("no id yet")
        }
    }

    function clearSessionStorage(newId) {
        clearValueFromSessionStorage('surveyData', surveyId)
        clearValueFromSessionStorage('surveyData', 'new')
        clearValueFromSessionStorage('userSurveyData', surveyId)
        clearValueFromSessionStorage('userSurveyData', 'new')
        if (newId) {
            clearValueFromSessionStorage('surveyData', newId)
            clearValueFromSessionStorage('userSurveyData', newId)
        }
    }

    const {setSnackbarData} = useSnackbar()

    // Display toast after navigating to a new survey that has just been published
    let location = useLocation()
    useEffect(()=> {
        if (location.state && location.state.hasOwnProperty("newSavedSurvey")) {
            if (location.state.newSavedSurvey) {
                clearSessionStorage()
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage(
                        {
                            id: 'toast.saveSuccess',
                            defaultMessage: 'Survey Saved',
                        }),
                    state: "success",
                    autoHide: true,
                })
            }
        }
        if (location.state && location.state.hasOwnProperty("surveyDuplicated")) {
            if (location.state.surveyDuplicated) {
                clearSessionStorage()
                setSnackbarData({
                    show: true,
                    text: intl.formatMessage(
                        {
                            id: 'toast.surveyDuplicated',
                            defaultMessage: 'Survey duplicated',
                        }),
                    state: "success",
                    autoHide: true,
                })
            }
        }
    },[location.state])


    //TODO: Refactor button functions and that statuses are properly set
    const saveSurvey = (newSurvey, draft) => {
        const newSurveyData = {...surveyData}
        // Clean branches of excess keys
        const cleanedSurveyData = removeUnnecessaryKeys(newSurveyData)

        cleanedSurveyData.updated = updateTimeToServerFormat(new Date())
        cleanedSurveyData.updatedby = authData.uid
        if (draft) {
            cleanedSurveyData.status = "DRAFT"
        } else {
            if (cleanedSurveyData.status === "DRAFT") {
                cleanedSurveyData.status = "SCHEDULED"
            }
        }
        setSurveyData(cleanedSurveyData)
        if (newSurvey) {
            cleanedSurveyData.id = newID
            const surveyDataForPost = reconstructConfigObject(cleanedSurveyData)
            surveyPost.mutate(surveyDataForPost, {
                onError: () => {
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'toast.saveFailed',
                                defaultMessage: 'The save failed. Please try again.',
                            }),
                        state: "error",
                        autoHide: true,
                    })
                },
                onSuccess: () => {
                    if (userSurveyData.length < 1) {
                        clearSessionStorage()
                        history.push(
                            {
                                pathname: "/survey/"+newID,
                                state: {newSavedSurvey: true}
                            }
                        )
                    } else {
                        const newAnnieUserSurveyData = [...userSurveyData]
                        newAnnieUserSurveyData.forEach( annieuser => annieuser.survey = newID)
                        annieUserSurveyPost.mutate(newAnnieUserSurveyData, {
                            onError: () => {
                                setSnackbarData({
                                    show: true,
                                    text: intl.formatMessage(
                                        {
                                            id: 'toast.saveFailed',
                                            defaultMessage: 'The save failed. Please try again.',
                                        }),
                                    state: "error",
                                    autoHide: true,
                                })
                            },
                            onSuccess: () => {
                                clearSessionStorage()
                                history.push(
                                    {
                                        pathname: "/survey/"+newID,
                                        state: {newSavedSurvey: true}
                                    }
                                )
                            }
                        })
                    }
                }
            })
        } else {
            const surveyDataForPost = reconstructConfigObject(cleanedSurveyData)
            surveyUpdate.mutate(surveyDataForPost, {
                onError: () => {
                    setSnackbarData({
                        show: true,
                        text: intl.formatMessage(
                            {
                                id: 'toast.saveFailed',
                                defaultMessage: 'The save failed. Please try again.',
                            }),
                        state: "error",
                        autoHide: true,
                    })
                },
                onSuccess: () => {
                    if (userSurveyData.length < 1) {
                        clearSessionStorage()
                        setShowPublishSummary(false)
                        setSnackbarData({
                            show: true,
                            text: intl.formatMessage(
                                {
                                    id: 'toast.saveSuccess',
                                    defaultMessage: 'Survey Saved!',
                                }),
                            state: "success",
                            autoHide: true,
                        })
                    } else {
                        annieUserSurveyPost.mutate(userSurveyData, {
                            onError: () => {
                                setSnackbarData({
                                    show: true,
                                    text: intl.formatMessage(
                                        {
                                            id: 'toast.saveFailed',
                                            defaultMessage: 'The save failed. Please try again.',
                                        }),
                                    state: "error",
                                    autoHide: true,
                                })
                            },
                            onSuccess: () => {
                                setOriginalUserSurveyData(_.cloneDeep(userSurveyData))
                                clearSessionStorage()
                                setShowPublishSummary(false)
                                setSnackbarData({
                                    show: true,
                                    text: intl.formatMessage(
                                        {
                                            id: 'toast.saveSuccess',
                                            defaultMessage: 'Survey Saved!',
                                        }),
                                    state: "success",
                                    autoHide: true,
                                })
                            }
                        })
                    }
                }
            })
        }
    }
    let draftText, saveText, modalSaveText
    draftText = intl.formatMessage(
        {
            id: 'survey.save.save',
            defaultMessage: 'Save',
        })
    saveText = intl.formatMessage(
        {
            id: 'survey.save.checkPublish',
            defaultMessage: 'Check & Publish',
        })
    modalSaveText = intl.formatMessage(
        {
            id: 'survey.save.publish',
            defaultMessage: 'Publish',
        })
    if (!surveyIsDraft) {
        draftText = intl.formatMessage(
            {
                id: 'survey.save.draft',
                defaultMessage: 'Change to draft',
            })
    }
    if (!surveyIsDraft && !createNewSurvey) {
        saveText = intl.formatMessage(
            {
                id: 'survey.save.checkSave',
                defaultMessage: 'Check & Save',
            })
        modalSaveText = intl.formatMessage(
            {
                id: 'survey.save.save',
                defaultMessage: 'Save',
            })
    }

    const {currentUserData} = useCurrentUserData()
    const userSuperAdmin = currentUserData.superuser && currentUserData.id.endsWith("@annieadvisor.com")

    const { showPopup, hidePopup } = usePopup()

    return <>
        <div className={"save-toolbar"}>
            <UndoRedoComponent />
            <button className={"test-survey"} onClick={()=>showPopup(<TestSurveyModal surveyConfig={surveyData} onClose={()=>hidePopup()}/>)}>
                <TestIcon />
                {intl.formatMessage({
                    defaultMessage: 'Send a Test',
                    id: 'sendATestSurvey'
                })}
            </button>
            {surveyUpdate.isLoading || surveyPost.isLoading ?
                <>
                    <button className={"draft"} disabled>
                        <LoaderIcon/>
                    </button>
                    <button className={"save"} disabled>
                        <LoaderIcon/>
                    </button>
                </> :
                <>
                    {(surveyIsScheduled || surveyIsDraft || surveyIsDeleted) &&
                    <button className={"draft"} onClick={() => {
                        saveSurvey(createNewSurvey, true)
                    }}>
                        {draftText}
                    </button>
                    }
                    {(surveyIsScheduled || surveyIsFinished) &&
                    <button className={"draft"} onClick={() => {
                        setShowDuplicatePopup(true)
                    }}>
                        {intl.formatMessage({
                            id: "survey.duplicate",
                            defaultMessage: "Duplicate"
                        })}
                    </button>
                    }
                    {!surveyIsDeleted &&
                    <button className={"save"} onClick={() => {
                        setShowPublishSummary(true)
                    }}>
                        {saveText}
                    </button>
                    }
                </>
            }
        </div>
        {showPublishSummary &&
        <Modal
            closeModal={()=>setShowPublishSummary(false)}
            header={intl.formatMessage(
                {
                    id: 'survey.overview',
                    defaultMessage: 'Survey Overview',
                })}
            confirmAction={()=>saveSurvey(createNewSurvey)}
            confirmText={modalSaveText}
            scrollContent={true}
            confirmDisabled={!surveyChecked}
            disabledText={<>
                {intl.formatMessage({
                    id: 'survey.overview.checkErrors',
                    defaultMessage: 'Check errors before publishing'
                })}
                {userSuperAdmin &&
                <button className={"override"} onClick={()=>setSurveyChecked(true)}>
                    {intl.formatMessage(
                        {
                            id: 'survey.overview.bypassTests',
                            defaultMessage: 'Skip checks',
                        })}
                </button>
                }
            </>}
        >
            <SurveySummary setSurveyChecked={surveyStatus => setSurveyChecked(surveyStatus)}/>
        </Modal>
        }
        {showDuplicatePopup &&
            <CopyPopup survey={reconstructConfigObject(surveyData)}
                       closePopup={()=>setShowDuplicatePopup(false)}
                       refetch={()=>console.log("Duplicate created")}
                       duplicateFromSurveyEditor={true}
                       setNewIdForNavigation={(newID)=> {
                           navigateToNewId(newID)
                       }}/>
        }
    </>
}

function UndoRedoComponent() {
    const intl = useIntl()
    const { anyEditorFocused } = useContext(EditorFocusContext)
    const dataContext = useDataContext()
    const { undo, redo, history, future } = dataContext

    const dataContextRef = useRef();
    dataContextRef.current = dataContext

    const handleKeyDown = (event) => {
        if (anyEditorFocused) return
        // Check for the appropriate key combinations
        if ((event.metaKey || event.ctrlKey) && event.key === 'z') {
            event.preventDefault(); // Prevent the default browser undo action
            if (event.shiftKey) {
                if (dataContextRef.current.future.length >= 1) {
                    dataContextRef.current.redo() // Redo on macOS with Shift+Cmd+Z
                }
            } else {
                if (dataContextRef.current.history.length >= 2) {
                    dataContextRef.current.undo() // Undo on macOS with Cmd+Z or Windows with Ctrl+Z
                }
            }
        }
        // Handle the Ctrl + Y (redo) for Windows/Linux
        else if (event.ctrlKey && event.key === 'y') {
            event.preventDefault(); // Prevent the default browser redo action
            if (dataContextRef.current.future.length >= 1) {
                dataContextRef.current.redo(); // Redo on Windows/Linux with Ctrl+Y
            }
        }
    }

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [anyEditorFocused])

    const undoText = intl.formatMessage({id:'undo',defaultMessage:'Undo'})
    const redoText = intl.formatMessage({id:'redo',defaultMessage:'Redo'})

    return <div className={"undo-redo"}>
        {history.length < 2 ?
            <button disabled title={undoText}><UndoIcon /></button> :
            <button title={undoText} onClick={undo}><UndoIcon /></button>
        }
        {future.length < 1 ?
            <button title={redoText} disabled><RedoIcon /></button> :
            <button title={redoText} onClick={redo}><RedoIcon /></button>
        }

        </div>
}